import React from 'react';
import type { FactorRosterMember } from '../../../../types/statusFactors';
import { useRosterStatus } from './StatusCell';
import RosterMemberIdentity from './RosterMemberIdentity';
import ContributionLine, { type ContributionLineProps } from './ContributionLine';

/**
 * MEMBER: the type avatar beside the sphere's name with its contribution line beneath, or the neutral placeholder when the caller cannot see it. Below the compact breakpoint (500px)
 * the STATUS column is hidden, so a second row carries its dot (shown by the stylesheet at that width).
 *
 * NOSONAR S6822: the status dot is a CSS-rendered circle (styled empty `<span>`), not a raster image —
 * there is no `<img>` src to point at. `role="img"` + `aria-label` is the WAI-ARIA pattern for a
 * CSS-drawn graphic with text semantics (https://www.w3.org/WAI/ARIA/apg/patterns/).
 */
const MemberCell: React.FC<Pick<ContributionLineProps, 'onPick' | 'onUseOverall'> & { member: FactorRosterMember }> = ({
  member,
  onPick,
  onUseOverall,
}) => {
  const { variant, label } = useRosterStatus(member);
  const dotKey = variant === 'measured' ? String(member.status) : variant;
  return (
    <span className="rollup-roster__member-stack">
      <RosterMemberIdentity hidden={member.hidden} name={member.sphereName} glyph={member.glyph} glyphColor={member.glyphColor} />
      <span className="rollup-roster__member-status" data-testid="roster-member-status-dot">
        <span className={`rollup-roster__status-dot rollup-roster__status-dot--${dotKey}`} role="img" aria-label={label} />
      </span>
      <ContributionLine member={member} onPick={onPick} onUseOverall={onUseOverall} />
    </span>
  );
};

export default MemberCell;
